import { useMemo } from "react";
import { Button } from "antd";

import { Handbook } from "features/handbook/model/handbook";
import { HandbookTable } from "features/handbook/ui/HandbookTable";
import { collectServiceCodes } from "features/handbook/lib/serviceCodes";
import { HandbookFieldFlags } from "features/handbook/lib/handbookFieldFlags";
import {
  HandbookColumnFlags,
  HandbookRefFields,
} from "features/handbook/ui/handbookColumns.types";
import styles from "./HandbookPage.module.css";

type PaginationState = { current: number; pageSize: number; total: number };

type ListProps = {
  rows: Handbook[];
  isFetching?: boolean;
  pagination: PaginationState;
  onPageChange: (page: number, pageSize: number) => void;
  onEdit: (row: Handbook) => void;
  onDelete: (row: Handbook) => void;
  isAgroCredit?: boolean;
};

/** В режиме привязки колонок меньше: БИК, банк, адрес и описания — только в общем списке. */
export const pickSharedFlags = (f: HandbookFieldFlags): HandbookColumnFlags => ({
  alias: f.hasAlias,
  unitMeasurement: f.hasUnitMeasurement,
  division: f.hasDivision,
  dataSourceStatus: f.hasDataSourceStatus,
  category: f.hasCategory,
  type: f.hasType,
  order: f.hasOrder,
  thresholdPerM2: f.hasThresholdPerM2,
  crop: f.hasCrop,
  region: f.hasRegion,
});

export const pickListFlags = (f: HandbookFieldFlags): HandbookColumnFlags => ({
  ...pickSharedFlags(f),
  bik: f.hasBik,
  bank: f.hasBank,
  address: f.hasAddress,
  descriptionRu: f.hasDescriptionRu,
  descriptionKy: f.hasDescriptionKy,
});

/** Имена полей-ссылок для рендера значений в ячейках. */
export const pickRefFields = (f: HandbookFieldFlags): HandbookRefFields => ({
  category: f.categoryFieldKey,
  type: f.typeFieldKey,
  crop: f.cropFieldKey,
  region: f.regionFieldKey,
  unitMeasurement: f.unitMeasurementFieldKey,
});

type BindingProps = {
  serviceTitle: string;
  serviceCode?: string;
  attached: ListProps;
  available: ListProps;
  flags: HandbookColumnFlags;
  refFields: HandbookRefFields;
  onAttachToService: (row: Handbook) => void;
  onDetachFromService: (row: Handbook) => void;
  onAttachAll: () => void;
  onDetachAll: () => void;
  isAddingAll?: boolean;
  isRemovingAll?: boolean;
};

/**
 * Режим привязки: слева записи сервиса, справа все доступные.
 * Записи, уже привязанные к сервису, подсвечиваются в правой таблице.
 */
export const HandbookBindingView = ({
  serviceTitle,
  serviceCode,
  attached,
  available,
  flags,
  refFields,
  onAttachToService,
  onDetachFromService,
  onAttachAll,
  onDetachAll,
  isAddingAll,
  isRemovingAll,
}: BindingProps) => {
  const attachedIds = useMemo(() => {
    if (!serviceCode) return [];
    return available.rows
      .filter((row) => collectServiceCodes(row).includes(String(serviceCode)))
      .map((row) => row.id)
      .filter((id): id is number => typeof id === "number");
  }, [available.rows, serviceCode]);

  const canBindAll = !!serviceCode && serviceCode !== "all";

  return (
    <div className={styles.tablesRow}>
      <div className={styles.content}>
        <div className={styles.availableHeader}>
          <h2>{serviceTitle}</h2>
          <Button
            type="primary"
            size="middle"
            style={{ marginLeft: 10 }}
            onClick={onDetachAll}
            loading={isRemovingAll}
            disabled={!canBindAll}
          >
            Отвязать все
          </Button>
        </div>
        <HandbookTable
          data={attached.rows}
          isFetching={attached.isFetching}
          pagination={attached.pagination}
          onPageChange={attached.onPageChange}
          onEdit={attached.onEdit}
          onDelete={attached.onDelete}
          isAgroCredit={attached.isAgroCredit}
          flags={flags}
          refFields={refFields}
          showDetachButton
          onDetachFromService={onDetachFromService}
        />
      </div>

      <div className={styles.content}>
        <div className={styles.availableHeader}>
          <h2>Доступные</h2>
          <Button
            type="primary"
            size="middle"
            style={{ marginLeft: 10 }}
            onClick={onAttachAll}
            loading={isAddingAll}
            disabled={!canBindAll}
          >
            Добавить все
          </Button>
        </div>
        <HandbookTable
          data={available.rows}
          isFetching={available.isFetching}
          pagination={available.pagination}
          onPageChange={available.onPageChange}
          onEdit={available.onEdit}
          onDelete={available.onDelete}
          isAgroCredit={available.isAgroCredit}
          flags={flags}
          refFields={refFields}
          showAttachButton
          onAttachToService={onAttachToService}
          attachedIds={attachedIds}
        />
      </div>
    </div>
  );
};

/** Режим одного списка: сервис не выбран, показываем весь справочник целиком. */
export const HandbookListView = ({
  list,
  flags,
  refFields,
}: {
  list: ListProps;
  flags: HandbookColumnFlags;
  refFields: HandbookRefFields;
}) => (
  <div className={styles.content}>
    <HandbookTable
      data={list.rows}
      isFetching={list.isFetching}
      pagination={list.pagination}
      onPageChange={list.onPageChange}
      onEdit={list.onEdit}
      onDelete={list.onDelete}
      isAgroCredit={list.isAgroCredit}
      flags={flags}
      refFields={refFields}
    />
  </div>
);
